import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api } from '../utils/api';
import type { SessionResponse, LoginRequest } from '../types';

interface AuthContextType {
  session: SessionResponse | null;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<void>;
  adminLogin: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      const data = await api.auth.getSession();
      setSession(data);
    } catch {
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const login = async (data: LoginRequest) => {
    const result = await api.auth.login(data);
    if (!result.session) throw new Error('Anmeldung fehlgeschlagen');
    setSession({
      authenticated: true,
      userType: result.session.userType,
      displayName: result.session.displayName,
      isAdmin: result.session.userType === 'admin',
    });
  };

  const adminLogin = async (password: string) => {
    const result = await api.auth.adminLogin(password);
    if (!result.session) throw new Error('Anmeldung fehlgeschlagen');
    setSession({
      authenticated: true,
      userType: result.session.userType,
      displayName: result.session.displayName,
      isAdmin: result.session.userType === 'admin',
    });
  };

  const logout = async () => {
    await api.auth.logout();
    setSession(null);
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        isLoading,
        login,
        adminLogin,
        logout,
        refreshSession,
        isAuthenticated: session?.authenticated ?? false,
        isAdmin: session?.isAdmin ?? false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth muss innerhalb eines AuthProvider verwendet werden');
  }
  return context;
}